import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ClipboardList, MapPin, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const CU_NAME = "Proposed Trillion Dollar Spending Power Federal Credit Union";
const inputCls = "w-full bg-transparent border border-bdu-gold/25 px-4 py-3 text-bdu-cream outline-none focus:border-bdu-gold";

function SampleProgress({ progress }) {
  if (!progress) return null;
  const pct = Math.min(100, Math.round((progress.counted / progress.target) * 100));
  return (
    <div className="mt-10 max-w-xl border border-bdu-gold/20 bg-bdu-surface2 p-5" data-testid="survey-progress">
      <div className="flex items-baseline justify-between gap-4">
        <span className="label-eyebrow">Community sample</span>
        <span className="text-sm text-bdu-cream/80 tabular-nums" data-testid="survey-progress-count">
          <span className="text-bdu-gold font-semibold">{progress.counted}</span> of {progress.target} neighbors
        </span>
      </div>
      <div className="mt-3 h-1.5 bg-bdu-gold/10 overflow-hidden">
        <div className="h-full bg-bdu-gold transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-3 text-xs text-bdu-cream/55 leading-relaxed">
        We need {progress.target} responses from Fulton and DeKalb residents for a {progress.confidence}% confidence,
        ±{progress.margin}% sample of the {progress.population.toLocaleString()} people the charter would serve.
      </p>
    </div>
  );
}

function Hero({ progress }) {
  return (
    <section className="border-b border-bdu-gold/10" data-testid="survey-hero">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 pt-20 pb-14">
        <div className="label-eyebrow">{CU_NAME} · Potential Member Survey</div>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05] max-w-4xl">
          Ten questions. <span className="text-bdu-gold italic">Three minutes.</span> One charter.
        </h1>
        <p className="mt-6 text-bdu-cream/70 max-w-2xl leading-relaxed">
          The NCUA asks every proposed credit union to show real demand from the community it would serve.
          Your answers are anonymous and go straight into our charter application.
        </p>
        <SampleProgress progress={progress} />
      </div>
    </section>
  );
}

function OptionChip({ label, selected, multi, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected}
      className={`text-left px-4 py-3 border text-sm flex items-center gap-3 transition-colors ${selected ? "border-bdu-gold bg-bdu-gold/10 text-bdu-cream" : "border-bdu-gold/20 text-bdu-cream/75 hover:border-bdu-gold/60"}`}>
      <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${multi ? "" : "rounded-full"} ${selected ? "border-bdu-gold bg-bdu-gold" : "border-bdu-gold/40"}`}>
        {selected && <span className={`w-1.5 h-1.5 bg-bdu-bg ${multi ? "" : "rounded-full"}`} />}
      </span>
      {label}
    </button>
  );
}

function QuestionField({ q, picked, onPick }) {
  const multi = q.max_choices > 1;
  const toggle = (opt) => {
    if (!multi) return onPick([opt]);
    if (picked.includes(opt)) return onPick(picked.filter((p) => p !== opt));
    if (picked.length >= q.max_choices) return toast.error(`Pick up to ${q.max_choices} for this question.`);
    onPick([...picked, opt]);
  };
  return (
    <fieldset className="border-t border-bdu-gold/10 pt-8" data-testid={`survey-question-${q.id}`}>
      <legend className="sr-only">{q.text}</legend>
      <div className="flex gap-4">
        <span className="font-serif text-3xl text-bdu-gold leading-none tabular-nums">{String(q.id + 1).padStart(2, "0")}</span>
        <p className="font-serif text-xl text-bdu-cream leading-snug">{q.text}</p>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-2">
        {q.options.map((opt, i) => (
          <OptionChip key={opt} label={opt} multi={multi} selected={picked.includes(opt)}
            onClick={() => toggle(opt)} testId={`survey-q${q.id}-option-${i}`} />
        ))}
      </div>
    </fieldset>
  );
}

function AboutYou({ form, setForm, ageRanges }) {
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      <label className="block">
        <span className="label-eyebrow">Home ZIP code *</span>
        <input required inputMode="numeric" maxLength={10} value={form.zip_code} placeholder="30310"
          onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className={`${inputCls} mt-3`} data-testid="survey-zip-input" />
      </label>
      <label className="block">
        <span className="label-eyebrow">Age range (optional)</span>
        <select value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })}
          className={`${inputCls} mt-3 bg-bdu-bg`} data-testid="survey-age-select">
          <option value="">Prefer not to say</option>
          {ageRanges.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form.website}
        onChange={(e) => setForm({ ...form, website: e.target.value })} />
    </div>
  );
}

function ThankYou({ result }) {
  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 py-24 text-center" data-testid="survey-thank-you">
      <CheckCircle2 size={44} className="mx-auto text-bdu-gold" />
      <h2 className="mt-6 font-serif text-4xl text-bdu-cream">Thank you. Your voice is on the record.</h2>
      <p className="mt-5 text-bdu-cream/70 leading-relaxed" data-testid="survey-thank-you-fom">
        {result.in_fom
          ? `Your ZIP is inside the proposed field of membership (${result.county} County). Your answers count toward the community sample.`
          : "Your ZIP is outside Fulton and DeKalb counties, so it won't count toward the county sample, but we've saved your answers."}
      </p>
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link to="/credit-union#subscribe" className="btn-gold" data-testid="survey-thank-you-pledge">Make a membership pledge</Link>
        <Link to="/field-of-membership" className="btn-ghost inline-flex items-center gap-2" data-testid="survey-thank-you-fom-link"><MapPin size={14} /> See the FOM map</Link>
      </div>
    </div>
  );
}

export default function MemberSurvey() {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ zip_code: "", age_range: "", website: "" });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/survey").then((r) => setMeta(r.data)).catch(() => toast.error("Couldn't load the survey. Please refresh."));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const missing = meta.questions.find((q) => !(answers[q.id] || []).length);
    if (missing) {
      toast.error(`Please answer question ${missing.id + 1}.`);
      document.querySelector(`[data-testid="survey-question-${missing.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/survey/responses", { ...form, answers });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen" data-testid="survey-page">
      {result ? <ThankYou result={result} /> : (
        <>
          <Hero progress={meta?.progress} />
          {!meta ? <div className="py-20 text-center text-bdu-cream/50">Loading survey…</div> : (
            <form onSubmit={submit} className="max-w-5xl mx-auto px-6 sm:px-10 py-16 space-y-10" data-testid="survey-form">
              <div className="label-eyebrow flex items-center gap-2"><ClipboardList size={12} /> About you</div>
              <AboutYou form={form} setForm={setForm} ageRanges={meta.age_ranges} />
              {meta.questions.map((q) => (
                <QuestionField key={q.id} q={q} picked={answers[q.id] || []}
                  onPick={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
              ))}
              <div className="border-t border-bdu-gold/10 pt-8 flex flex-wrap items-center justify-between gap-6">
                <p className="text-[11px] text-bdu-cream/45 italic max-w-xl leading-relaxed" data-testid="survey-compliance-note">
                  {CU_NAME}, pending NCUA charter approval. Completing this survey is anonymous and creates no
                  commitment to join, deposit or borrow.
                </p>
                <button type="submit" disabled={busy} className="btn-gold disabled:opacity-50" data-testid="survey-submit-btn">
                  <Send size={14} /> {busy ? "Sending…" : "Submit my answers"}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
